/**
 * A screen declares its read contract ONCE here: a `GET /api/screens/…` whose response is strict at every depth, always
 * carries `freshness`, names its regions (the parts a caller may not be allowed to see), says which live topics it
 * depends on and states its budget. It is built on `defineRoute`: the contract holds the route it created
 * (`contract.route`), so a screen can be registered and called with the one mechanism of routes.
 *
 * Like `defineRoute`, it checks the declaration when it is DEFINED and every error names the screen id and the field,
 * never a data value. The types are inferred: `ScreenData` and `ScreenQuery` are the only way to type a screen.
 */

import { z } from 'zod/v4'

import { describeOpenSchemaFindings, findOpenSchemas } from '../contract/closed-schema.js'
import { defineScreenRoute } from '../contract/define-route.js'
import type {
  AnyRouteContract,
  RouteAccess,
  RouteContract,
  UntypedInputFields,
} from '../contract/define-route.js'

import { freshnessSchema } from './freshness.js'
import {
  isForbiddenRegionVariant,
  isRegionShaped,
  isScreenRegion,
  regionRequirementSchema,
} from './region.js'
import type { RegionRequirement } from './region.js'

const SCREEN_PATH_PREFIX = '/api/screens/'

/** Type-only (no value exists at runtime): only `defineScreen` produces something that carries it. */
declare const screenContractBrand: unique symbol

/** A screen is built from at most this many requests (its own, not those of the shell). */
const SCREEN_MAX_REQUESTS = 3

/**
 * The budget a screen commits to. `statements` counts the statements of the screen's own composition (not the fixed
 * ones of the request) and `p95Ms` is a warning threshold, not a limit the contract enforces.
 */
const screenBudgetSchema = z.strictObject({
  // The cap is checked by `defineScreen` with a message that names the screen: it lives in one place.
  requests: z.number().int().min(1),
  statements: z.number().int().min(1),
  bytes: z.number().int().min(1),
  p95Ms: z.number().int().min(1),
})
export type ScreenBudget = z.infer<typeof screenBudgetSchema>

type ScreenObjectSchema = z.ZodObject<z.core.$ZodShape, z.core.$ZodObjectConfig>

export interface ScreenInput {
  /** `shell`, `admin.audit.activity`. */
  id: string
  path: `/api/screens/${string}`
  access: RouteAccess
  query?: ScreenObjectSchema
  /** A `z.strictObject` that includes `freshness: freshnessSchema`. */
  response: ScreenObjectSchema
  /** Keys of `response` wrapped with `screenRegion`, with what each requires. */
  regions?: Record<string, RegionRequirement>
  /** Live topics the screen depends on; `[]` when none. */
  dependsOn: readonly string[]
  budget: ScreenBudget
  summary: string
}

type ScreenRouteInput<I extends ScreenInput> = {
  id: `screens.${I['id']}`
  method: 'GET'
  path: I['path']
  access: I['access']
  response: I['response']
  summary: string
  tags: readonly string[]
} & (I extends { query: infer Q extends ScreenObjectSchema } ? { query: Q } : unknown)

export interface ScreenContract<I extends ScreenInput = ScreenInput> {
  readonly [screenContractBrand]: true
  readonly id: I['id']
  readonly path: I['path']
  readonly access: I['access']
  readonly query: I extends { query: infer Q extends ScreenObjectSchema } ? Q : undefined
  readonly response: I['response']
  readonly regions: Readonly<Record<string, RegionRequirement>>
  readonly dependsOn: readonly string[]
  readonly budget: ScreenBudget
  readonly summary: string
  readonly route: RouteContract<ScreenRouteInput<I>>
}

/**
 * What a function that accepts ANY screen contract takes. Written out (not `ScreenContract<ScreenInput>`) because in
 * the input `query` is optional: computed from it, `query` would be `undefined` and a screen that declares one would
 * not be assignable.
 */
export interface AnyScreenContract {
  readonly [screenContractBrand]: true
  readonly id: string
  readonly path: `/api/screens/${string}`
  readonly access: RouteAccess
  readonly query: ScreenObjectSchema | undefined
  readonly response: ScreenObjectSchema
  readonly regions: Readonly<Record<string, RegionRequirement>>
  readonly dependsOn: readonly string[]
  readonly budget: ScreenBudget
  readonly summary: string
  readonly route: AnyRouteContract
}

/** Distributes over `S['query']`: a contract whose query is `Q | undefined` answers `output<Q> | Record<string, never>`. */
type QueryOutput<Q> = Q extends z.ZodType ? z.output<Q> : Record<string, never>
export type ScreenQuery<S extends AnyScreenContract> = QueryOutput<S['query']>
export type ScreenData<S extends AnyScreenContract> = z.output<S['response']>

const SCREEN_ID_PATTERN = /^[a-z][a-zA-Z0-9]*(\.[a-z][a-zA-Z0-9]*)*$/

const contractsById = new Map<string, AnyScreenContract>()

/** Every screen defined so far, by id. */
export const screenContracts: ReadonlyMap<string, AnyScreenContract> = contractsById

/** True when a union has, among its options, the `forbidden` variant `screenRegion` built. */
function carriesForbiddenVariant(schema: z.ZodType): boolean {
  const options = (schema._zod.def as unknown as { options?: readonly z.ZodType[] }).options
  return Array.isArray(options) && options.some(isForbiddenRegionVariant)
}

function isStrictObject(schema: z.ZodType): schema is ScreenObjectSchema {
  const def = schema._zod.def as unknown as { type: string; catchall?: z.ZodType }
  return def.type === 'object' && def.catchall?._zod.def.type === 'never'
}

type ResponseShape<I extends ScreenInput> = I['response']['shape']
type RegionsNotInResponse<I extends ScreenInput> = Exclude<
  keyof NonNullable<I['regions']>,
  keyof ResponseShape<I>
>

type ResponseCheck<I extends ScreenInput> = string extends keyof ResponseShape<I>
  ? unknown
  : ([RegionsNotInResponse<I>] extends [never]
      ? unknown
      : { readonly __regionsNotInResponse: RegionsNotInResponse<I> }) &
      (ResponseShape<I> extends { freshness: typeof freshnessSchema }
        ? unknown
        : { readonly __responseWithoutFreshness: true })

/**
 * A `z.coerce.*` field of the query with no declared input: `defineScreen` builds its route inside, so the check
 * `defineRoute` makes on its own input does not see it.
 */
type QueryCheck<I extends ScreenInput> = [UntypedInputFields<I['query']>] extends [never]
  ? unknown
  : { readonly __untypedQueryFields: UntypedInputFields<I['query']> }

/**
 * Compile-time twin of the runtime checks, so the error is where the declaration is written: a region that is not a
 * field of the response, a response without `freshness: freshnessSchema` and an untyped `z.coerce` in the query. A
 * widened response (`string` keys) says nothing about the first two and is left to the runtime checks.
 */
type ScreenInputCheck<I extends ScreenInput> = ResponseCheck<I> & QueryCheck<I>

export function defineScreen<const I extends ScreenInput>(
  input: I & ScreenInputCheck<I>,
): ScreenContract<I> {
  const { id, path, budget, response } = input
  const regions = input.regions ?? {}

  if (!SCREEN_ID_PATTERN.test(id)) {
    throw new Error(`defineScreen: invalid id "${id}"`)
  }
  if (!path.startsWith(SCREEN_PATH_PREFIX)) {
    throw new Error(`defineScreen ${id}: path "${path}" must start with ${SCREEN_PATH_PREFIX}`)
  }

  if (budget.requests > SCREEN_MAX_REQUESTS) {
    throw new Error(
      `defineScreen ${id}: budget.requests is ${budget.requests}, the cap is ${SCREEN_MAX_REQUESTS}`,
    )
  }
  const budgetCheck = screenBudgetSchema.safeParse(budget)
  if (!budgetCheck.success) {
    const field = budgetCheck.error.issues[0]?.path.join('.') || 'budget'
    throw new Error(`defineScreen ${id}: invalid budget at ${field}`)
  }

  if (!isStrictObject(response)) {
    throw new Error(`defineScreen ${id}: response must be a z.strictObject`)
  }
  if (response.shape.freshness !== freshnessSchema) {
    throw new Error(`defineScreen ${id}: response.freshness must be freshnessSchema`)
  }

  // One walk finds the open spots and every `screenRegion` at any depth.
  const regionNodes: Array<{ path: string; node: z.ZodType }> = []
  const forbiddenPaths: string[] = []
  const shapedPaths: string[] = []
  const open = findOpenSchemas(response, {
    root: 'response',
    closure: 'strict',
    jsonOnly: true,
    visitNode: (node, path) => {
      if (isScreenRegion(node)) regionNodes.push({ path, node })
      if (isForbiddenRegionVariant(node)) forbiddenPaths.push(path)
      // A union with the shape of a region that `screenRegion()` did not build: neither the instance it returned
      // nor a clone of it (a clone still carries its `forbidden` variant and has its own message below).
      if (isRegionShaped(node) && !isScreenRegion(node) && !carriesForbiddenVariant(node)) {
        shapedPaths.push(path)
      }
    },
  })
  if (open.length > 0) {
    throw new Error(
      `defineScreen ${id}: open response schema at ${describeOpenSchemaFindings(open)}; every object must be a z.strictObject, every value must be JSON (no Date, bigint, Map or Set) and a free JSON document must use jsonDocument({ reason })`,
    )
  }

  // A clone of a region is another instance (not registered) that still carries the registered `forbidden` variant.
  const registeredPaths = new Set(regionNodes.map((region) => region.path))
  for (const forbiddenPath of forbiddenPaths) {
    if (!registeredPaths.has(forbiddenPath)) {
      throw new Error(
        `defineScreen ${id}: region at ${forbiddenPath} is not the one screenRegion() returned (a clone made with .describe(), .meta() or .refine() is not a region)`,
      )
    }
  }

  if (shapedPaths.length > 0) {
    throw new Error(
      `defineScreen ${id}: field at ${shapedPaths[0]} has the shape of a region (state 'ready' | 'forbidden') but was not built with screenRegion()`,
    )
  }

  for (const [key, requirement] of Object.entries(regions)) {
    if (!regionRequirementSchema.safeParse(requirement).success) {
      throw new Error(
        `defineScreen ${id}: region "${key}" has an invalid requirement (a non-empty permission, or { role: 'super_admin' }, and nothing else)`,
      )
    }
  }

  for (const key of Object.keys(regions)) {
    // `shape` holds the core type of zod; the classic `ZodType` is what `isScreenRegion` takes.
    const field = response.shape[key] as z.ZodType | undefined
    if (field === undefined) {
      throw new Error(`defineScreen ${id}: region "${key}" is not a field of the response`)
    }
    if (!isScreenRegion(field)) {
      throw new Error(`defineScreen ${id}: region "${key}" is not wrapped with screenRegion()`)
    }
  }
  for (const { path: regionPath, node } of regionNodes) {
    const direct = Object.entries(response.shape).find(
      ([key, field]) => field === node && regionPath === `response.${key}`,
    )
    if (direct === undefined) {
      throw new Error(
        `defineScreen ${id}: region at ${regionPath} must be a direct field of the response (not nested, optional or inside a list)`,
      )
    }
    const key = direct[0]
    if (!Object.hasOwn(regions, key)) {
      throw new Error(
        `defineScreen ${id}: field "${key}" is wrapped with screenRegion() but has no entry in regions`,
      )
    }
  }

  if (contractsById.has(id)) {
    throw new Error(`defineScreen: duplicate screen id "${id}"`)
  }

  const route = defineScreenRoute({
    id: `screens.${id}`,
    method: 'GET',
    path,
    access: input.access,
    ...(input.query ? { query: input.query } : {}),
    response,
    summary: input.summary,
    tags: ['screens'],
  })

  // The type is computed from `I`; the runtime shape is the same object, frozen, with every key present.
  const contract = Object.freeze({
    id,
    path,
    access: input.access,
    query: input.query,
    response,
    regions: Object.freeze(
      Object.fromEntries(
        Object.entries(regions).map(([key, requirement]) => [
          key,
          Object.freeze({ ...requirement }),
        ]),
      ),
    ),
    dependsOn: Object.freeze([...input.dependsOn]),
    budget: Object.freeze({ ...budget }),
    summary: input.summary,
    route,
  }) as unknown as ScreenContract<I>

  contractsById.set(id, contract as unknown as AnyScreenContract)
  return contract
}
